/**
 * BotDetail.jsx — one bot: stats, recent trades, controls.
 *
 * Three stat cells rather than an equity curve. Charts would mean recharts,
 * which roughly triples the bundle, and on a phone "12 trades · 66% win ·
 * $742 volume" is more legible than a sparkline.
 *
 * Stop and pause are here because their worst case is lost opportunity. A
 * compromised Telegram session that stops your bots costs nothing; one that
 * withdraws costs everything — which is why withdrawal is refused by the
 * proxy rather than merely absent from this screen.
 */

import React, { useEffect, useState } from "react";
import * as api from "../lib/api";
import * as tg from "../lib/telegram";
import { C, money, pnlColor, Label, Panel, Button, Empty } from "../theme";

export default function BotDetail({ botId, onBack }) {
  const [bot, setBot] = useState(null);
  const [trades, setTrades] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [b, t] = await Promise.all([
          api.getBot(botId),
          api.botTrades(botId).catch(() => []),
        ]);
        setBot(b);
        setTrades(Array.isArray(t) ? t : t?.trades || []);
        setErr(null);
      } catch (e) {
        setErr(e.message);
      }
    };
    load();
    return api.poll(load, 10000);
  }, [botId]);

  const act = async (fn, verb) => {
    // Confirm through Telegram's native dialog rather than window.confirm —
    // it matches the platform and is far harder to mis-tap.
    const ok = await tg.confirm(`${verb} this bot?`);
    if (!ok) return;
    setBusy(true);
    try {
      await fn(botId);
      tg.haptic.ok();
      setBot(await api.getBot(botId));
    } catch (e) {
      tg.haptic.err();
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (err && !bot) return <Empty><span style={{ color: C.red }}>{err}</span></Empty>;
  if (!bot) return <Empty>Loading…</Empty>;

  const pnl = Number(bot.total_profit_loss || 0);
  const running = bot.status === "running";

  return (
    <div style={{ padding: 14 }}>

      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "flex-start", marginBottom: 14 }}>
        <div>
          <div style={{ fontFamily: C.disp, fontSize: 32, lineHeight: 1,
                        color: pnlColor(pnl) }}>
            {money(pnl)}
          </div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>
            {bot.bot_name || bot.trader_name} · {String(bot.mode || "").toUpperCase()}
          </div>
        </div>
        <span style={{ fontSize: 10, letterSpacing: "0.08em", padding: "3px 8px",
                       color: running ? C.green : C.dim,
                       border: `1px solid ${running ? C.green + "59" : C.border}` }}>
          {String(bot.status || "").toUpperCase()}
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1,
                    background: C.border, border: `0.5px solid ${C.border}`,
                    marginBottom: 14 }}>
        <Stat label="TRADES" value={bot.total_copied_trades || 0} />
        <Stat label="WIN" value={bot.win_rate != null
          ? `${Number(bot.win_rate).toFixed(0)}%` : "—"} />
        <Stat label="VOLUME" value={money(bot.total_volume, false)} />
      </div>

      <Label>RECENT TRADES</Label>
      {trades.length === 0 ? (
        <Panel style={{ marginBottom: 14 }}>
          <span style={{ fontSize: 12, color: C.dim }}>No trades yet.</span>
        </Panel>
      ) : (
        <div style={{ border: `0.5px solid ${C.border}`, background: C.bg1,
                      marginBottom: 14 }}>
          {trades.slice(0, 8).map((t, i) => {
            const p = Number(t.total_profit_loss ?? t.profit_loss ?? 0);
            return (
              <div key={t.trade_id || i}
                   style={{ display: "flex", justifyContent: "space-between",
                            padding: "9px 11px",
                            borderBottom: i < 7
                              ? `0.5px solid rgba(245,166,35,0.12)` : "none" }}>
                <div style={{ minWidth: 0, paddingRight: 8 }}>
                  <div style={{ fontSize: 11.5, color: C.ink,
                                overflow: "hidden", textOverflow: "ellipsis",
                                whiteSpace: "nowrap" }}>
                    {t.market_name || t.market_slug || "—"}
                  </div>
                  <div style={{ fontSize: 10, color: C.dim }}>
                    {t.outcome} {t.price != null ? Number(t.price).toFixed(2) : ""}
                  </div>
                </div>
                <span style={{ fontSize: 12, color: pnlColor(p), alignSelf: "center" }}>
                  {money(p)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {err && (
        <p style={{ fontSize: 11.5, color: C.red, margin: "0 0 12px" }}>{err}</p>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 7 }}>
        <Button variant="ghost" disabled={busy || !running}
                onClick={() => act(api.pauseBot, "Pause")}>
          Pause
        </Button>
        <Button variant="danger" disabled={busy || bot.status === "stopped"}
                onClick={() => act(api.stopBot, "Stop")}>
          Stop
        </Button>
      </div>

      <p style={{ fontSize: 10.5, color: C.dim, lineHeight: 1.7,
                  marginTop: 14, textAlign: "center" }}>
        Editing configuration, withdrawals and key export are on the web.
      </p>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div style={{ background: C.bg1, padding: "9px 8px" }}>
    <div style={{ fontSize: 9.5, color: C.dim, letterSpacing: "0.1em",
                  marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 15, color: C.ink }}>{value}</div>
  </div>
);
